import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import EntityForm from "@/components/EntityForm";
import SupplierDetailDialog from "@/components/SupplierDetailDialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Truck, MoreVertical, Pencil, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";

const SUPPLIER_TYPES = ["Fabricant", "Grossiste", "Distributeur", "Prestataire", "Importateur", "Autre"];

export default function Suppliers() {
  const [items, setItems] = useState(null);
  const [settings, setSettings] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = () => api.get("/suppliers").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/settings").then((r) => setSettings(r.data)); }, []);

  const schema = [
    { name: "nom", label: "Nom / Raison sociale", required: true, col: 2 },
    { name: "type", label: "Type de fournisseur", type: "select", options: SUPPLIER_TYPES },
    { name: "contact_nom", label: "Personne de contact" },
    { name: "telephone", label: "Téléphone" },
    { name: "email", label: "E-mail", type: "email" },
    { name: "site_web", label: "Site internet" },
    { name: "ville", label: "Ville", type: "select", options: settings.cities || [] },
    { name: "pays", label: "Pays", type: "select", options: settings.countries || [] },
    { name: "adresse", label: "Adresse", col: 2 },
    { name: "nif", label: "NIF" },
    { name: "rccm", label: "RCCM" },
    { name: "categories", label: "Catégories de produits fournis", col: 2 },
    { name: "conditions_paiement", label: "Conditions de paiement" },
    { name: "delais_livraison", label: "Délais de livraison" },
    { name: "devise", label: "Devise" },
    { name: "remises", label: "Remises éventuelles" },
    { name: "conditions_commerciales", label: "Conditions commerciales", type: "textarea", col: 2 },
    { name: "observations", label: "Observations", type: "textarea", col: 2 },
  ];

  const save = async (form) => {
    try {
      if (editing) await api.put(`/suppliers/${editing.id}`, form);
      else await api.post("/suppliers", form);
      toast.success(editing ? "Fournisseur mis à jour" : "Fournisseur créé"); load();
    } catch (e) { toast.error(apiErr(e)); throw e; }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ce fournisseur ?")) return; await api.delete(`/suppliers/${id}`); toast.success("Supprimé"); load(); };

  return (
    <div>
      <PageHeader title="Fournisseurs" subtitle="Fiches détaillées, historique & documents">
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-supplier" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau fournisseur</Button>
      </PageHeader>
      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={Truck} title="Aucun fournisseur" subtitle="Ajoutez vos fournisseurs pour gérer les achats." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>N°</TableHead><TableHead>Nom</TableHead><TableHead>Type</TableHead><TableHead>Contact</TableHead><TableHead>Téléphone</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((s) => (
                <TableRow key={s.id} data-testid={`supplier-row-${s.id}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => setDetailId(s.id)}>
                  <TableCell className="font-mono text-xs text-slate-500">{s.numero}</TableCell>
                  <TableCell className="font-semibold">{s.nom}</TableCell>
                  <TableCell className="text-slate-600">{s.type || "—"}</TableCell>
                  <TableCell className="text-slate-600">{s.contact_nom || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{s.telephone || "—"}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`supplier-menu-${s.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setDetailId(s.id)} data-testid={`supplier-view-${s.id}`}><Eye className="h-4 w-4 mr-2" />Voir la fiche</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setEditing(s); setOpen(true); }}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(s.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}
      <EntityForm open={open} onOpenChange={setOpen} title={editing ? "Modifier le fournisseur" : "Nouveau fournisseur"} schema={schema} initial={editing} onSubmit={save} />
      <SupplierDetailDialog supplierId={detailId} onOpenChange={(o) => !o && setDetailId(null)} />
    </div>
  );
}
